<template>
  <div class="edit-phone-container">
    <!-- Current Phone Display -->
    <div class="flex items-center gap-1 current-phone mb-4">
      <div class="w-1/2">
        <div class="relative">
     
          <!-- Desktop Input (hidden on mobile) -->
          <input
            v-if="!isMobile"
            v-model="phoneWithoutPrefix"
            type="text"
            inputmode="text"
            @input="onPhoneInput"
            @keydown="onPhoneKeyDown"
            :placeholder="t('newPhoneNumber')"
            class="w-full h-14 pl-3 pr-3 border border-[#e6e6e6] rounded-3xl text-left ltr"
          />
          <!-- Mobile Input (hidden on desktop) -->
          <input
            v-else
            v-model="phoneWithoutPrefix"
            type="number"
            inputmode="numeric"
            pattern="[0-9]*"
            @input="onPhoneInput"
            @keydown="onPhoneKeyDown"
            :placeholder="t('newPhoneNumber')"
            class="w-full h-14 pl-3 pr-3 border border-[#e6e6e6] rounded-3xl text-left ltr"
          />
        </div>
      </div>
      <div class="w-1/2 current-phone-value flex items-center px-4 py-2 bg-gray-100 rounded-[32px] h-14">
        {{ smsir.currentPhone || t('noPhoneNumber') }}
      </div>
    </div>

    <!-- New Phone Input -->
   

    <!-- Verify Button -->
    <button
      v-if="showVerifyButton"
      type="button"
      @click="requestOtp"
      :disabled="loading"
      class="change-phone-button mt-3 w-full flex justify-center items-center py-2 px-4 border border-transparent text-sm font-medium rounded-[32px] h-14 text-white bg-main hover:contrast-150 focus:outline-none focus:ring-0"
    >
      <span v-if="loading" class="spinner-loading"></span>
      <span v-else>{{ t('changePhone') }}</span>
    </button>

    <!-- OTP Input Section -->
    <div v-if="showOtpSection" class="otp-input-section mt-4">
      <otp-input
        v-model="otpCode"
        :resend-delay="settings.settings?.resend_delay || 60"
        @resend="handleResendOtp"
        :otp-length="settings.settings?.otp_length || 4"
        @complete="verifyOtp"
        ref="otpInputRef"
      />
      
      <div class="flex gap-4 mt-3">
        <button
          @click="handleBack"
          class="back-button flex-1 flex justify-center items-center py-2 px-4 border border-main text-sm font-medium rounded-[32px] h-14 text-black bg-white focus:outline-none focus:ring-0"
        >
          {{ t('back') }}
        </button>
        <button
          @click="verifyOtp"
          :disabled="loading"
          class="verify-button flex-1 flex justify-center items-center py-2 px-4 border border-transparent text-sm font-medium rounded-[32px] h-14 text-white bg-main hover:contrast-150 focus:outline-none focus:ring-0"
        >
          <span v-if="loading" class="spinner-loading"></span>
          <span v-else>{{ t('verify') }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, onMounted, computed, onUnmounted } from 'vue'
import OtpInput from '../auth/OtpInput.vue'
import { showSuccess, showError } from '../../utils/swal'
import { t } from '../../utils/translate'
import { convertToEnglishNumbers, isPersianOrArabicNumber } from '../../utils/number'

const $t = t; // Create a local alias for the t function to make migration easier

const smsir = window.smsir || {}
const $ = window.jQuery

const phone = ref('')
const phoneWithoutPrefix = ref('')
const isValidPhone = ref(false)
const loading = ref(false)
const showOtpSection = ref(false)
const otpCode = ref('')
const verifiedPhoneInput = ref(null)
const otpInputRef = ref(null)
const settings = ref({})

// Add device detection logic
const isMobile = ref(false)

watch(phoneWithoutPrefix, (newValue) => {
  if (newValue) {
    // For validation, ensure it starts with 9
    const validationValue = newValue.startsWith('0') ? newValue.substring(1) : newValue
    
    // Update the full phone number with prefix
    phone.value = `${newValue}`
    
    // Validate phone number (must be 10 digits for Iranian mobile starting with 9)
    isValidPhone.value = newValue.length === 11 && newValue.startsWith('9') || 
                        (newValue.length === 10 && newValue.startsWith('9'))
  } else {
    phone.value = ''
    isValidPhone.value = false
  }
}, { immediate: true })

onMounted(() => {
  const currentPhone = smsir.currentPhone || ''
  if (currentPhone) {
    const cleanNumber = currentPhone.replace(/^\+98|^98|^0/, '')
    phoneWithoutPrefix.value = cleanNumber
    phone.value = `${cleanNumber}`
    isValidPhone.value = cleanNumber.length === 10 && cleanNumber.startsWith('9')
  }
  
  verifiedPhoneInput.value = document.getElementById('verified-phone')
  if (verifiedPhoneInput.value) {
    verifiedPhoneInput.value.value = currentPhone
  }
  
  fetchSettings()
  // Check if mobile on initial load
  checkDeviceType()
  // Add resize listener to detect device changes
  window.addEventListener('resize', checkDeviceType)
})

// Clean up event listener when component is unmounted
onUnmounted(() => {
  window.removeEventListener('resize', checkDeviceType)
})

const checkDeviceType = () => {
  // Use a more reliable approach to detect mobile devices
  if (typeof window !== 'undefined') {
    // Use window.innerWidth as the primary detection method
    isMobile.value = window.innerWidth < 768
    
    // Additional check for mobile user agents as a fallback
    if (!isMobile.value) {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      isMobile.value = /android|webos|iphone|ipad|ipod|blackberry|IEMobile|Opera Mini/i.test(userAgent);
    }
  } else {
    // Default to desktop if window is not available
    isMobile.value = false;
  }
}

// Force initial value to false for desktop
isMobile.value = false;

const showVerifyButton = computed(() => {
  return isValidPhone.value && phone.value && phone.value !== smsir.currentPhone
})

const fetchSettings = async () => {
  try {
    const response = await $.post(smsir.ajaxUrl, {
      action: 'smsir_get_settings',
      nonce: smsir.nonce
    })
    if (response.success) {
      settings.value = response.data
    } else {
      console.error('Failed to load settings:', response.data?.message)
    }
  } catch (err) {
    console.error('Error fetching settings:', err)
  }
}

const validatePhone = (valid, number, country, e) => {
  isValidPhone.value = valid
}

const onPhoneInput = (event) => {
  let value = event.target.value
  
  // Convert Persian/Arabic numbers to English
  value = convertToEnglishNumbers(value)
  
  // Keep only digits
  value = value.replace(/\D/g, '')
  
  // Handle the first digit - must be 9
  if (value.length > 0) {
    // First character must be 9
    if (value[0] !== '9') {
      // If first digit is wrong - replace value with either:
      // 1. Empty string if no valid prefix exists
      // 2. Previous valid value starting with 9
      if (phoneWithoutPrefix.value && phoneWithoutPrefix.value.startsWith('9')) {
        // Keep the previous valid value
        event.preventDefault()
        return
      } else {
        // If the input was 0, try to interpret as starting with 09
        if (value.startsWith('0') && value.length > 1) {
          // Remove the leading 0 and check if the next digit is 9
          value = value.substring(1)
          if (!value.startsWith('9')) {
            // If still doesn't start with 9, prevent
            phoneWithoutPrefix.value = ''
            showError(t('phoneStartWith9'))
            return
          }
        } else {
          // For any other first digit, prevent and show error
          phoneWithoutPrefix.value = ''
          showError(t('phoneStartWith9'))
          return
        }
      }
    }
  }
  
  // Enforce max length
  if (value.length > 10) {
    value = value.slice(0, 10)
  }
  
  // Update input value
  phoneWithoutPrefix.value = value
}

// Add keydown event handler to prevent non-9 first digit
const onPhoneKeyDown = (event) => {
  const input = event.target
  const value = input.value
  
  // If input is empty and key pressed is not 9 or 0 (for 09...)
  if (value.length === 0 && !/[90]/.test(event.key) && /^\d$/.test(event.key)) {
    event.preventDefault()
    showError(t('phoneStartWith9'))
  }
}

const requestOtp = async () => {
  try {
    if (!phone.value || !isValidPhone.value) {
      showError(t('pleaseEnterValidPhone'))
      return
    }

    loading.value = true
    const response = await $.post(smsir.ajaxUrl, {
      action: 'smsir_request_change_phone_otp',
      phone: phone.value,
      nonce: smsir.nonce
    })

    if (response.success) {
      showOtpSection.value = true
      showSuccess(response.data.message || t('otpSent'))
    } else {
      throw new Error(response.data?.message || t('generalError'))
    }
  } catch (err) {
    showError(err.message || t('generalError'))
  } finally {
    loading.value = false
  }
}

const verifyOtp = async () => {
  try {
    loading.value = true
    const response = await $.post(smsir.ajaxUrl, {
      action: 'smsir_verify_change_phone',
      phone: phone.value,
      otp: otpCode.value,
      nonce: smsir.nonce
    })

    if (response.success) {
      if (verifiedPhoneInput.value) {
        verifiedPhoneInput.value.value = phone.value
      }
      showOtpSection.value = false
      showSuccess(t('phoneVerified'))
    } else {
      throw new Error(response.data?.message || t('invalidOtp'))
    }
  } catch (err) {
    showError(err.message || t('generalError'))
  } finally {
    loading.value = false
  }
}

const handleResendOtp = async () => {
  await requestOtp()
  if (otpInputRef.value) {
    otpInputRef.value.startCountdown()
  }
}

const handleBack = () => {
  showOtpSection.value = false
  otpCode.value = ''
}
</script>

<style scoped>
.edit-phone-container {
  max-width: 100%;
}

.current-phone-value {
  direction: ltr;
  text-align: left;
}

.ltr {
  direction: ltr;
}

input[type="text"]::-webkit-outer-spin-button,
input[type="text"]::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

input[type="text"][type="number"] {
  -moz-appearance: textfield;
  appearance: textfield;
}
</style>